import { forwardRef, memo, useEffect, useRef, type Ref } from 'react';
import { Column, Text } from '~/commonComponents';
import { InputSearchOffice } from '~/components/PermanentJobsV2/components/InputSearchOffice/InputSearchOffice';
import { getShouldUseParentPostShiftForChild } from '~/growthbook';
import { useUserSelector } from '~/hooks';
import { useIsParentOfficeSelector } from '~/hooks/useIsParentOfficeSelector';

interface IOfficeFieldProps {
  value?: string;
  onChange?: (value: string) => void;
}

const Component = ({ value, onChange }: IOfficeFieldProps, ref: Ref<HTMLDivElement>) => {
  const isParentOffice = useIsParentOfficeSelector();
  const shouldShowDropdown = getShouldUseParentPostShiftForChild() && isParentOffice;

  const user = useUserSelector();
  const fallbackOfficeName = user.office_name;
  const fallbackOfficeId = user.id;

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!shouldShowDropdown && fallbackOfficeId) {
      onChangeRef.current?.(fallbackOfficeId);
    }
  }, [shouldShowDropdown, fallbackOfficeId]);

  if (shouldShowDropdown) {
    return (
      <div ref={ref}>
        <Column data-testid="office-field" gap={8}>
          <Text bold fontSize={18} color="neutral_600">
            Office:
          </Text>
          <InputSearchOffice value={value} onChange={(v) => onChange?.(v)} />
        </Column>
      </div>
    );
  }

  return (
    <div ref={ref}>
      <Column data-testid="office-field" gap={8}>
        <Text bold fontSize={18} color="neutral_600">
          Office:
        </Text>
        <Text
          fontSize={16}
          color="neutral_600"
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
          data-testid="office-static-pill"
        >
          {fallbackOfficeName}
        </Text>
      </Column>
    </div>
  );
};

export const OfficeField = memo(forwardRef(Component));
